import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { signIn } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await signIn(email, password)
    } catch (err) {
      if (err instanceof Error) {
        // Provide specific error messages for different scenarios
        if (err.message.includes('비활성화') || err.message.includes('정지')) {
          // Account is inactive or suspended
          setError(err.message)
        } else if (err.message.includes('사용자 정보를 찾을 수 없습니다')) {
          // User info not found
          setError('계정 정보를 불러올 수 없습니다. 관리자에게 문의하세요.')
        } else if (
          err.message.includes('Invalid login credentials') ||
          err.message.includes('Invalid email or password')
        ) {
          // Invalid credentials
          setError('이메일 또는 비밀번호가 올바르지 않습니다.')
        } else if (err.message.includes('Email not confirmed')) {
          // Email not confirmed
          setError('이메일 인증이 필요합니다. 이메일을 확인해주세요.')
        } else {
          // Other errors
          setError(err.message)
        }
      } else {
        setError('로그인에 실패했습니다. 다시 시도해주세요.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light font-display">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-4xl">lock</span>
            </div>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900">
            Project Phoenix
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            문제 관리 시스템에 로그인하세요
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>
              <span className="text-sm">{error}</span>
            </div>
          )}
          <div className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                이메일 주소
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-gray-400 text-xl">mail</span>
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input block w-full pl-10 pr-3 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                비밀번호
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-gray-400 text-xl">key</span>
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input block w-full pl-10 pr-3 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="••••••••"
                />
              </div>
              <div className="flex justify-end mt-1">
                <button
                  type="button"
                  onClick={() => navigate('/reset-password')}
                  className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  비밀번호를 잊으셨나요?
                </button>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-transparent rounded-lg text-sm font-bold tracking-wide text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <span className="material-symbols-outlined animate-spin">progress_activity</span>
                <span>로그인 중...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined">login</span>
                <span>로그인</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
